import { Box, Grid, Tooltip } from "@mui/material";
import { FC } from "react";
import { Colors } from "../../../../configs/colors";
import { CustomButton } from "../../../atoms";

export interface IServiceTabButtonProps {
  name: string;
  isSelected: boolean;
  handleOnClick(): void;
  disabled?: boolean;
  /**
   * "service" is a priced service tab; "addon" (Alteration) works on top of
   * the selected service, so it gets its own dark style and sub label.
   */
  variant?: "service" | "addon";
  /** Second line, e.g. the service an add-on applies to */
  subLabel?: string;
  /** Tooltip explaining why the tab is disabled */
  disabledHint?: string;
}

const ServiceTabButton: FC<IServiceTabButtonProps> = ({
  name,
  isSelected,
  handleOnClick,
  disabled = false,
  variant = "service",
  subLabel,
  disabledHint,
}) => {
  const isAddon = variant === "addon";
  const idleBackground = isAddon ? Colors.MAIN_GRAY : Colors.PRIMARY;

  return (
    // The wrapper (not the disabled button) receives hover/touch for the hint
    <Tooltip title={disabled && disabledHint ? disabledHint : ""} placement="top" enterTouchDelay={0}>
      <Grid
        onClick={disabled ? () => {} : () => handleOnClick()}
        style={{
          textDecoration: "none",
          width: "9.85rem",
          height: "5.14rem",
          display: "inline-flex",
          flexShrink: 0,
          flexDirection: "row",
          cursor: disabled ? "not-allowed" : "pointer",
          color: Colors.WHITE,
        }}
      >
        <CustomButton
          sx={{
            width: "9.85rem",
            maxWidth: "9.85rem",
            // Long names ("Fur, Leather & Suede") wrap to two lines instead of
            // spilling out of the button
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            background: isSelected ? Colors.SECONDARY : idleBackground,
            color: Colors.WHITE,
            minWidth: "9.85rem",
            height: "5.14rem",
            borderRadius: "0.71rem",
            textTransform: "capitalize",
            fontSize: "1.42rem",
            lineHeight: "1.42rem",
            padding: "0.57rem",
            ...(isAddon && {
              flexDirection: "column",
              gap: "0.21rem",
              border: `0.14rem solid ${isSelected ? Colors.SECONDARY : Colors.MAIN_GRAY}`,
            }),
            "&:hover": {
              background: `${isSelected ? Colors.SECONDARY : idleBackground} !important`,
            },
            "&:disabled": isAddon
              ? {
                  // An add-on that can't be used yet: outlined, not filled
                  background: Colors.WHITE,
                  color: Colors.ACCENT_GRAY_DISABLED,
                  border: `0.14rem dashed ${Colors.ACCENT_GRAY_DISABLED}`,
                }
              : {
                  background: Colors.ACCENT_GRAY,
                  color: Colors.WHITE,
                },
          }}
          disabled={disabled}
          name={`service-tab-button-${name}`}
          aria-label={subLabel ? `${name} for ${subLabel}` : name}
          aria-pressed={isSelected}
        >
          {isAddon ? (
            <>
              <Box component="span" sx={{ lineHeight: 1 }}>
                {name}
              </Box>
              {subLabel && (
                <Box
                  component="span"
                  sx={{
                    fontSize: "var(--fs-2xs)",
                    lineHeight: 1.2,
                    fontWeight: 500,
                    opacity: 0.85,
                    textTransform: "none",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {subLabel}
                </Box>
              )}
            </>
          ) : (
            name
          )}
        </CustomButton>
      </Grid>
    </Tooltip>
  );
};

export default ServiceTabButton;
